import { CategoriesResponse, FlatCategory } from '@shopby/shop-sdk'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'

import { isMobile } from 'react-device-detect'
import TagManager, { DataLayerArgs } from 'react-gtm-module'
import { Link, useNavigate } from 'react-router-dom'
import { EventDetailProduct, SectionDetailStickerInfo } from '../../@types/home'
import { AFWebLinkType, RNPostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { findObjectsWithValue, onErrorImg, plusComma } from '../../js/script'
import { imgURL } from '../../pages/Home'
import { useAppContext } from '../provider/AppProvider'
import { IconStarOn } from '../svg/IconSvg'

interface Accumulator {
  images: SectionDetailStickerInfo[]
  texts: SectionDetailStickerInfo[]
}

interface HotEventItemProps {
  el: EventDetailProduct
  listId: number
  listName: string
  category: CategoriesResponse | null
  idx: number
  promotion_id: number
  promotion_name: string
}

const HotEventItem = ({ el, listId, listName, category, idx }: HotEventItemProps) => {
  const { isAppView } = useAppContext()
  const navigate = useNavigate()

  const [image, setImage] = useState<SectionDetailStickerInfo[]>([])
  const [text, setText] = useState<SectionDetailStickerInfo[]>([])
  const [categories, setCategories] = useState<Array<FlatCategory>>([])

  // FIXME: 해당 로직도 다수의 컴포넌트에서 사용 시 공통 처리하시고 import 하셔서 사용하시길 바랍니다. @choieungeon
  const categorizeStickers = (acc: Accumulator, element: SectionDetailStickerInfo): Accumulator => {
    if (element.type === 'IMAGE') acc.images.push(element)
    else acc.texts.push(element)
    return acc
  }

  useEffect(() => {
    if (el) {
      // 초기화
      setImage([])
      setText([])
      const categoryNo = el.displayCategoryNos.split('|')

      // 스티커 정보 처리
      const { images, texts } = el.stickerInfos?.reduce(categorizeStickers, { images: [], texts: [] }) || {
        images: [],
        texts: [],
      }
      setImage(images)
      setText(texts)

      // 카테고리 처리
      const newItems = categoryNo.flatMap((no: string | number) =>
        findObjectsWithValue(category?.flatCategories, Number(no))
      )
      const filteredCategories = newItems.filter((cat: FlatCategory) => cat.depth1Label !== '브랜드')
      setCategories(filteredCategories)
    }
  }, [el, category])

  const clickHandler = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      const selectItem = {
        item_id: String(el?.productNo),
        item_name: el?.productName,
        affiliation: '어바웃피싱',
        item_list_price: el?.salePrice,
        item_selling_price: el?.salePrice - el?.immediateDiscountAmt,
        price: el?.salePrice - el?.immediateDiscountAmt,
        item_total_discount: el?.immediateDiscountAmt,
        discount: 0,
        index: idx,
        item_brand: el?.brandName,
        item_category: categories?.[0]?.depth1Label,
        item_category2: categories?.[0]?.depth2Label,
        item_category3: categories?.[0]?.depth3Label,
        item_list_id: String(listId),
        item_list_name: listName,
      }

      const gtmClickItem: DataLayerArgs = {
        dataLayer: {
          event: 'select_item',
          ecommerce: {
            items: [selectItem],
          },
        },
      }

      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: 'select_item',
          eventType: {
            items: [selectItem],
          },
        })
      } else {
        TagManager.dataLayer(gtmClickItem)
      }
      if (isAppView) {
        RNPostMessage(AFWebLinkType.moveToLink, {
          url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/product/${
            el.productNo
          }?items=${JSON.stringify([selectItem])}`,
        })
      } else {
        navigate(`/product/${el.productNo}`, {
          state: { items: [selectItem] },
        })
      }
    },
    [categories, el, idx, listId, listName, isAppView, navigate]
  )

  const discountPercent = useMemo(() => Math.round((el?.immediateDiscountAmt / el?.salePrice) * 100), [el])
  const discountedPrice = useMemo(() => el?.salePrice - el?.immediateDiscountAmt, [el])

  return (
    <li>
      <div className='item_wrap'>
        <div className='item_img'>
          {image.map((element, i) => (
            <div className='item_flag' key={`${element.label}-${i}`}>
              <img src={element.label} alt={element.label} />
            </div>
          ))}
          <div onClick={clickHandler}>
            <div className='dim'></div>
            <img src={el?.imageUrls[0]} alt='' onError={onErrorImg} />
          </div>
        </div>
        <div className='item_txt'>
          <div onClick={clickHandler}>
            <div className='item_brand_title'>{el?.brandName}</div>
            <div className='item_title'>{el?.productName}</div>
            {discountPercent === 0 ? (
              <div className='item_sale_wrap'>
                <p className='price'>{plusComma(discountedPrice)}원</p>
              </div>
            ) : (
              <>
                <div className='item_origin_price'>{plusComma(el?.salePrice)}원</div>
                <div className='item_sale_wrap'>
                  <p className='percent'>{discountPercent}%</p>
                  <p className='price'>{plusComma(discountedPrice)}원</p>
                </div>
              </>
            )}
            <div className='item_star_rating'>
              <IconStarOn width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />
              <span className='review_rating'>{el?.reviewRating}</span>
              <span className='review_num'>({el?.totalReviewCount})</span>
              <div className='npay'>
                <img src={`${imgURL}/icon_npay.png`} alt='Naver Pay' />
              </div>
            </div>
            {text.length !== 0 && (
              <div className='item_txt_flag'>
                {text.map((el, i) => (
                  <span className='delvery' key={`${el?.label}-${i}`}>
                    {el?.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

export default HotEventItem
